import { createAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { AccountRecord, CoreMsgTypes, UserAccountRecord } from '@tassat/fred-definitions';
import { AccountRecordExtended } from '@tassat/fred-extended-definitions';
import {
    Dictionary, groupBy, map, mapValues, union
} from 'lodash';

export interface AccountsState {
    [s: string]: AccountRecordExtended;
}

const initialState: AccountsState = {};

const associateAccountsWithUserAccounts = (userAccounts: Dictionary<UserAccountRecord>,
    accounts: Dictionary<AccountRecord>): Dictionary<AccountRecordExtended> => {
    const groupedUserAccountsByAccountId: Dictionary<UserAccountRecord[]> = groupBy(userAccounts, userAccount => userAccount.AccountId);
    const mapgroupedUserAccountsByAccountId: Dictionary<string[]> = mapValues(groupedUserAccountsByAccountId, (value) => map(value, 'UserAccountId'));

    return mapValues(accounts, (value) => ({
        ...value,
        UserAccounts: mapgroupedUserAccountsByAccountId[value.AccountId] ?? []
    }));
};

const initialLoadAction = createAction<{ accounts: Dictionary<AccountRecord>; userAccounts: Dictionary<UserAccountRecord> }>('InitialLoad');
const newACGMappingAction = createAction<{ accounts: Dictionary<AccountRecord>; userAccounts: Dictionary<UserAccountRecord> }>('NewACGMapping');
const userAccountRecordAction = createAction<UserAccountRecord>(CoreMsgTypes.UserAccountRecord);
const accountRecordAction = createAction<AccountRecord>(CoreMsgTypes.AccountRecord);

const accountsSlice = createSlice({
    name: 'accounts',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(userAccountRecordAction, (state, action) => {
                if (!state[action.payload.AccountId]) {
                    state[action.payload.AccountId] = { UserAccounts: [action.payload.UserAccountId]} as AccountRecordExtended;
                } else {
                    state[action.payload.AccountId].UserAccounts =
                        union([ ...(state[action.payload.AccountId].UserAccounts ?? []), action.payload.UserAccountId ]);
                }
            })
            .addCase(accountRecordAction, (state, action) => {
                if (!state[action.payload.AccountId]) {
                    state[action.payload.AccountId] = {
                        UserAccounts: [],
                        ...action.payload
                    } as AccountRecordExtended;
                } else {
                    Object.assign(state[action.payload.AccountId], action.payload);
                }
            })
            .addMatcher(isAnyOf(initialLoadAction, newACGMappingAction), (state, action) => {
                const data = associateAccountsWithUserAccounts(action.payload.userAccounts, action.payload.accounts);
                Object.assign(state, data);
            });
    }
});

export default accountsSlice.reducer;